/** Same-origin cookie changes carry invalidation only, never session data. */
export function createSessionChannel(name: string) {
  const storageKey = `trioty:session:${name}`;
  const pending = new Map<string, number>();
  let onPause: (() => void) | undefined;
  let onInspect: (() => Promise<void>) | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let revision = 0;
  let blocked = false;
  let installed = false;
  let disposed = false;
  const renewals = new Set<ReturnType<typeof setInterval>>();
  const channel = typeof window !== "undefined" && typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(storageKey) : undefined;
  type Message = { id: string; phase: "begin" | "end"; until: number };
  function send(message: Message) {
    channel?.postMessage(message);
    try { localStorage.setItem(storageKey, JSON.stringify(message)); } catch { /* BroadcastChannel remains available when storage is disabled. */ }
  }
  function schedule() {
    clearTimeout(timer);
    if (!pending.size) { void inspect(); return; }
    timer = setTimeout(() => {
      for (const [id, until] of pending) if (until <= Date.now()) pending.delete(id);
      schedule();
    }, Math.max(1, Math.min(...pending.values()) - Date.now()));
  }
  async function inspect() {
    if (disposed || !blocked || pending.size || !onInspect) return;
    const current = ++revision;
    for (const delay of [0, 1000, 3000]) {
      if (delay) await new Promise(resolve => setTimeout(resolve, delay));
      if (current !== revision || pending.size) return;
      try { await onInspect(); if (current === revision) blocked = false; return; } catch { /* Unknown cookie binding stays paused. Focus/online retries the read only. */ }
    }
  }
  function receive(value: unknown) {
    if (disposed || !value || typeof value !== "object") return;
    const message = value as Partial<Message>;
    if (typeof message.id !== "string" || message.id.length > 64 || typeof message.until !== "number" || !Number.isFinite(message.until)) return;
    if (message.phase === "begin") {
      const newOperation = !pending.has(message.id);
      pending.set(message.id, Math.min(message.until, Date.now() + 15_000));
      blocked = true;
      revision++;
      if (newOperation) onPause?.();
      schedule();
    } else if (message.phase === "end" && pending.delete(message.id)) schedule();
  }
  const onMessage = (event: MessageEvent) => receive(event.data);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== storageKey || !event.newValue) return;
    try { receive(JSON.parse(event.newValue)); } catch { /* Invalid storage content is not a session event. */ }
  };
  const onRecovery = () => { void inspect(); };
  return {
    install(pause: () => void, inspectBinding: () => Promise<void>) {
      onPause = pause; onInspect = inspectBinding;
      if (installed || typeof window === "undefined") return;
      installed = true;
      channel?.addEventListener("message", onMessage);
      window.addEventListener("storage", onStorage);
      window.addEventListener("focus", onRecovery);
      window.addEventListener("online", onRecovery);
    },
    dispose() { disposed = true; clearTimeout(timer); for (const renewal of renewals) clearInterval(renewal); renewals.clear(); channel?.removeEventListener("message", onMessage); channel?.close(); if (typeof window !== "undefined") { window.removeEventListener("storage", onStorage); window.removeEventListener("focus", onRecovery); window.removeEventListener("online", onRecovery); } pending.clear(); revision++; },
    recover() { blocked = true; void inspect(); },
    begin() {
      const id = messageId();
      const announce = () => send({ id, phase: "begin", until: Date.now() + 15_000 });
      announce();
      const renewal = setInterval(announce, 5000);
      renewals.add(renewal);
      return () => { clearInterval(renewal); renewals.delete(renewal); if (disposed) return; send({ id, phase: "end", until: Date.now() }); };
    },
  };
}

/**
 * A unique id for one session-change announcement.
 *
 * NOT `crypto.randomUUID()` alone: browsers expose it only in a SECURE context
 * (HTTPS or localhost). On plain `http://` over a LAN IP — a phone testing the
 * dev server — it is undefined, `begin()` threw before the login request was
 * even sent, and every login form reported it as "wrong phone or password".
 * `getRandomValues` exists in every context, and the id only has to be unique
 * among a few tabs' messages, not a standards-shaped UUID.
 */
function messageId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

